import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Images, Navigation, Phone, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { HomeMap } from "@/components/map/HomeMap";
import { HomeAssistant } from "@/components/ai/HomeAssistant";
import { MortgageCalculator } from "@/components/calculators/MortgageCalculator";
import { EnquiryForm } from "@/components/forms/EnquiryForm";
import { SaveButton } from "@/components/listings/ListingCard";
import { Container, SampleBadge } from "@/components/common/ui";
import { useBases } from "@/hooks/useData";
import { api } from "@/lib/api";
import { directionsUrl, miles, money } from "@/lib/format";
import { SITE } from "@/lib/site";

const Gallery = ({ photos, title }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <div className="grid h-[300px] grid-cols-4 grid-rows-2 gap-2 overflow-hidden rounded-md sm:h-[460px]" data-testid="listing-gallery">
        {photos.slice(0, 5).map((p, i) => (
          <button key={p} onClick={() => setOpen(true)} className={`overflow-hidden bg-neutral-100 ${i === 0 ? "col-span-4 row-span-2 sm:col-span-2" : "hidden sm:block"}`} data-testid={`gallery-photo-${i}`}>
            <img src={p} alt={`${title} photo ${i + 1}`} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
          </button>
        ))}
      </div>
      <button onClick={() => setOpen(true)} data-testid="gallery-view-all" className="absolute bottom-4 right-4 flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[13px] shadow-lg">
        <Images className="h-4 w-4" /> View all {photos.length} photos
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl border-0 bg-black p-0 sm:rounded-md" data-testid="gallery-dialog">
          <DialogTitle className="sr-only">{title} photos</DialogTitle>
          <Carousel className="w-full">
            <CarouselContent>
              {photos.map((p, i) => (
                <CarouselItem key={p}><img src={p} alt={`${title} photo ${i + 1}`} className="max-h-[80vh] w-full object-contain" /></CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-3" data-testid="gallery-prev" />
            <CarouselNext className="right-3" data-testid="gallery-next" />
          </Carousel>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DistanceTable = ({ listing, selected, onSelect }) => (
  <Table data-testid="distance-table">
    <TableHeader>
      <TableRow><TableHead>Installation</TableHead><TableHead className="text-right">Straight-line</TableHead><TableHead className="text-right">Drive</TableHead></TableRow>
    </TableHeader>
    <TableBody>
      {listing.distances.map((d) => (
        <TableRow key={d.id} onClick={() => onSelect(d.id)} data-testid={`distance-row-${d.id}`}
          className={`cursor-pointer ${selected === d.id ? "bg-neutral-100" : ""}`}>
          <TableCell><span className="font-medium text-ink">{d.short}</span><span className="block text-[12px] text-neutral-500">{d.branch}</span></TableCell>
          <TableCell className="text-right font-mono text-[13px]">{miles(d.miles)}</TableCell>
          <TableCell className="text-right">
            <a href={directionsUrl(listing, d)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`directions-${d.id}`}
              className="inline-flex items-center gap-1 text-[13px] text-ink underline underline-offset-4 hover:decoration-brass">
              <Navigation className="h-3 w-3" /> Route
            </a>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const Fact = ({ label, value }) => (
  <div className="border-b border-neutral-200 py-3"><p className="text-[13px] text-neutral-500">{label}</p><p className="text-[15px] text-ink">{value ?? "—"}</p></div>
);

export default function ListingPage() {
  const { key } = useParams();
  const bases = useBases();
  const [l, setL] = useState(null);
  const [err, setErr] = useState(false);
  const [baseId, setBaseId] = useState(null);

  useEffect(() => {
    setL(null);
    api.get(`/listings/${key}`).then((r) => { setL(r.data); setBaseId(r.data.distances[0].id); }).catch(() => setErr(true));
  }, [key]);

  const sel = l?.distances.find((d) => d.id === baseId);
  const focus = useMemo(() => (l && sel ? { bounds: [[Math.min(l.lng, sel.lng), Math.min(l.lat, sel.lat)], [Math.max(l.lng, sel.lng), Math.max(l.lat, sel.lat)]], padding: 90, maxZoom: 13.5, id: `${l.listing_key}-${sel.id}` } : null), [l, sel]);
  const link = useMemo(() => (l && sel ? { from: l, to: sel, label: `${miles(sel.miles)} to ${sel.short}` } : null), [l, sel]);

  if (err) return <Container className="py-24 text-center" ><p className="text-2xl" data-testid="listing-not-found">This home isn't available.</p><Link to="/search" className="mt-4 inline-block underline">Back to search</Link></Container>;
  if (!l) return <Container className="py-24"><div className="h-[460px] animate-pulse rounded-md bg-neutral-100" data-testid="listing-loading" /></Container>;

  const share = async () => {
    await navigator.clipboard?.writeText(window.location.href).catch(() => {});
    toast("Link copied");
  };

  return (
    <div className="pb-20 pt-6" data-testid="listing-page">
      <Container>
        <div className="mb-4 flex items-center justify-between">
          <Link to="/search" className="flex items-center gap-2 text-[14px] text-neutral-600 hover:text-ink" data-testid="listing-back"><ArrowLeft className="h-4 w-4" /> Back to search</Link>
          <div className="flex items-center gap-2">
            <button onClick={share} className="flex items-center gap-2 rounded-full border border-neutral-300 px-4 py-2 text-[13px]" data-testid="listing-share"><Share2 className="h-4 w-4" /> Share</button>
            <SaveButton listingKey={l.listing_key} className="border border-neutral-300 shadow-none" />
          </div>
        </div>
        <Gallery photos={l.photos} title={l.title} />

        <div className="mt-10 grid gap-12 lg:grid-cols-[1fr_380px]">
          <div className="min-w-0">
            {l.sample && (
              <div className="mb-6 flex items-center gap-3 rounded-md bg-mist p-4 text-[14px] text-neutral-700" data-testid="listing-sample-notice">
                <SampleBadge /> This is a demonstration listing, not a real property for sale. Live REIN listings replace these once the feed is connected.
              </div>
            )}
            <p className="text-4xl font-medium text-ink" data-testid="listing-price">{money(l.price)}</p>
            <p className="mt-2 text-[16px] text-ink">{l.beds} beds · {l.baths} baths · {l.sqft?.toLocaleString()} sqft{l.lot_acres ? ` · ${l.lot_acres} acres` : ""}</p>
            <p className="mt-1 text-[15px] text-neutral-500">{l.title} · {l.neighborhood}, {l.city}, {l.state} {l.postcode}</p>
            <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-[13px] text-white" data-testid="listing-nearest-base">
              <Navigation className="h-3.5 w-3.5 text-brass" /> {miles(l.nearest_base.miles)} to {l.nearest_base.short} (nearest installation)
            </p>
            <HomeAssistant key={l.listing_key} listing={l} />

            <h2 className="mt-12 text-2xl font-normal">Overview</h2>
            <p className="mt-3 text-[16px] leading-relaxed text-neutral-700">{l.description}</p>
            {l.features?.length > 0 && (
              <ul className="mt-5 grid gap-2 sm:grid-cols-2">{l.features.map((f) => <li key={f} className="flex items-center gap-2 text-[15px]"><span className="h-1.5 w-1.5 rounded-full bg-brass" />{f}</li>)}</ul>
            )}
            <div className="mt-8 grid grid-cols-2 gap-x-8 sm:grid-cols-3">
              <Fact label="Home type" value={l.property_type} />
              <Fact label="Year built" value={l.year_built} />
              <Fact label="Lot" value={l.lot_acres ? `${l.lot_acres} acres` : "—"} />
              <Fact label="HOA" value={l.hoa_monthly ? `${money(l.hoa_monthly)}/mo` : "None"} />
              <Fact label="Days on market" value={l.days_on_market} />
              <Fact label="MLS #" value={l.mls_number || "Shown with live REIN data"} />
            </div>

            <h2 className="mt-14 text-2xl font-normal" id="commute">Commute to every installation</h2>
            <p className="mt-2 text-[15px] text-neutral-600">Tap an installation to draw the line in 3D. “Route” opens live driving directions.</p>
            <div className="mt-6 grid gap-6 xl:grid-cols-[1.25fr_1fr]">
              <div className="h-[420px] overflow-hidden rounded-md">
                <HomeMap testId="listing-map" listings={[l]} bases={bases} selectedBaseId={baseId} activeKey={l.listing_key} linkTarget={link} focus={focus}
                  onBaseClick={setBaseId} initialView={{ center: [l.lng, l.lat], zoom: 11.5, pitch: 55, bearing: -15 }} initialMode="satellite" />
              </div>
              <div className="max-h-[420px] overflow-y-auto rounded-md border border-neutral-200"><DistanceTable listing={l} selected={baseId} onSelect={setBaseId} /></div>
            </div>

            <h2 className="mt-14 text-2xl font-normal" id="payment">Estimated payment</h2>
            <div className="mt-6"><MortgageCalculator defaultPrice={l.price} hoa={l.hoa_monthly || 0} showBah testId="listing-calculator" /></div>
            {l.listing_office && <p className="mt-8 text-[13px] text-neutral-500">Listing courtesy of {l.listing_office}. Data provided by REIN, Inc. Information deemed reliable but not guaranteed.</p>}
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-md border border-neutral-200 p-6 shadow-[0_18px_40px_-24px_rgba(0,0,0,0.35)]" data-testid="tour-card">
              <p className="text-xl font-medium">Request a tour</p>
              <p className="mb-5 mt-1 text-[14px] text-neutral-500">In person, or from out of town before your house-hunting leave.</p>
              <EnquiryForm kind="tour" listingKey={l.listing_key} extras={["preferredDate"]} dense submitLabel="Request tour" testIdPrefix="tour"
                messagePlaceholder={`I'd like to see ${l.title} in ${l.neighborhood}.`} />
              <a href={SITE.phoneMainHref} className="mt-3 flex h-12 items-center justify-center gap-2 rounded-full border border-ink text-[15px] hover:bg-ink hover:text-white" data-testid="tour-call">
                <Phone className="h-4 w-4" /> Call {SITE.phoneMain}
              </a>
            </div>
          </aside>
        </div>
      </Container>
    </div>
  );
}
